import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import {
  CategoriaRecordatorio,
  IDocumentacion,
  IFilter,
  IQueryParam,
  IRecordatorio,
} from 'modelos/src';
import { firstValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { fechaCelda } from '../../../../auxiliares/tabla/celdas';
import { AuxiliaresModule } from '../../../../auxiliares/auxiliares.module';
import { DialogService } from '../../../../auxiliares/dialog/dialog.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { DocumentosService } from '../../../../auxiliares/servicios/http/documentos.service';
import {
  ParamsService,
  IParamsService,
} from '../../../../auxiliares/servicios/params.service';
import { IRegExpSearch } from '../../../../auxiliares/tabla/filtro/filtro.component';
import {
  IColumnas,
  ICrearBoton,
} from '../../../../auxiliares/tabla/tabla.component';
import { CrearEditarDocumentoComponent } from '../crear-editar-documento/crear-editar-documento.component';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { RecordatoriosService } from '../../../../auxiliares/servicios/http/recordatorios.service';

@Component({
  imports: [AuxiliaresModule],
  selector: 'app-listado-documentos',
  templateUrl: './listado-documentos.component.html',
  styleUrl: './listado-documentos.component.scss',
})
export class ListadoDocumentosComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  helper = inject(HelperService);
  service = inject(DocumentosService);
  private dialogService = inject(DialogService);
  private formularios = inject(FormularioDialogService);
  private paramsService = inject(ParamsService);
  private activatedRoute = inject(ActivatedRoute);
  private recordatorioService = inject(RecordatoriosService);

  @Input() idEntidad?: string;
  @Input() idCliente?: string;
  @Input() tipoEntidad?:
    | 'Activo'
    | 'Vehiculo'
    | 'Colectivo'
    | 'Luminaria'
    | 'Alarma';
  //// Tabla nueva
  public id?: string | null;
  public tipo?: string | null;
  public categoria?: CategoriaRecordatorio;
  public form?: FormGroup;
  public columnas?: IColumnas<IDocumentacion>[];
  public name = 'ListadoDocumentosComponent';
  public documentos?: IDocumentacion[];
  public documentos$?: Subscription;
  public totalCount = 0;

  public botonCrear: ICrearBoton = {
    mostrar: this.helper.tienePermiso('Vehículos - Crear documentos conductor'),
    tooltip: 'Crear documento',
    color: 'primary',
    tipo: 'material',
    icon: 'add',
    accion: () => this.crear(),
  };

  public search: IRegExpSearch = {
    fields: ['tipo'],
  };

  public filter: IFilter<IDocumentacion> = {};

  public query: IQueryParam = {
    filter: JSON.stringify(this.filter),
    sort: 'nombre',
    page: 0,
    limit: +this.helper.getPageSize(this.name),
    includeChildren: true,
    childrenLevel: 0,
  };

  public async listar(): Promise<void> {
    if (this.query.filter) {
      const filter: IFilter<IDocumentacion> = JSON.parse(this.query.filter);
      if (this.tipo == 'Usuario') filter.idChofer = this.id;
      if (this.tipo == 'Vehiculo') filter.idActivo = this.id;
      if (this.tipo == 'Colectivo') filter.idActivo = this.id;
      this.query.filter = JSON.stringify(filter);
    }
    this.documentos$?.unsubscribe();
    this.documentos$ = this.lista2
      .documentos(this.query, 30)
      .subscribe((data) => {
        this.totalCount = data.totalCount;
        this.documentos = data.datos;
      });
  }

  // Acciones
  public async crear() {
    const param: IParamsService = {
      idEntidad: this.id,
      tipoEntidad: this.tipo,
      categoria: this.categoria,
    };
    this.paramsService.setParams(param);
    const res = this.formularios.abrir(CrearEditarDocumentoComponent, {
      tamano: 'mediano',
    });
    if (res) {
      res.afterClosed().subscribe(async (data) => {
        if (data) {
          await this.listar();
        }
      });
    }
  }

  public async editar(documento: IDocumentacion) {
    const param: IParamsService = {
      documento: documento,
    };
    this.paramsService.setParams(param);
    const res = this.formularios.abrir(CrearEditarDocumentoComponent, {
      tamano: 'mediano',
    });
    if (res) {
      res.afterClosed().subscribe(async (data) => {
        if (data) {
          await this.listar();
        }
      });
    }
  }

  public async eliminar(dato: IDocumentacion) {
    const res = await this.dialogService.confirm(
      'Eliminar documento',
      `¿Querés eliminar el documento?`,
    );
    if (!res) return;
    if (dato._id) {
      await this.service.delete(dato._id);

      //Traemos el recordatorio asociado y lo eliminamos
      const recordFilter: IFilter<IRecordatorio> = {
        idDocumentacion: dato._id,
      };
      const query = {
        filter: JSON.stringify(recordFilter),
        limit: 1,
        select: '_id',
      };
      const recordatorioAsociado =
        await this.recordatorioService.getFiltered(query);
      await this.recordatorioService.delete(recordatorioAsociado.datos[0]._id);

      this.helper.notifSuccess('Documento eliminado');
      await this.listar();
    }
  }

  // Hooks

  private setColumnas() {
    this.columnas = [
      {
        header: {
          label: 'Tipo',
          sortable: true,
        },
        row: {
          field: 'tipo',
          noData: 'Sin tipo',
        },
      },
      {
        header: {
          label: 'Fecha de emisión',
          sort: 'emision',
          sortable: true,
        },
        row: {
          // Es una fecha sin hora: sólo el día
          parse: (dato) =>
            dato.emision ? fechaCelda(dato.emision).slice(0, 10) : undefined,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Fecha de vencimiento',
          sort: 'vencimiento',
          sortable: true,
        },
        row: {
          // Es una fecha sin hora: sólo el día
          parse: (dato) =>
            dato.vencimiento ? fechaCelda(dato.vencimiento).slice(0, 10) : undefined,
          noData: 'Sin fecha',
        },
      },
      {
        header: {
          label: 'Descripción',
        },
        row: {
          parse(dato) {
            if (dato.descripcion) {
              let text = dato.descripcion;
              if (text.length >= 50) {
                text = text.slice(0, 50);
                text += '...';
              }
              return text;
            }
            return '';
          },
          // La descripción completa, en el tooltip
          tooltip: (dato) => dato.descripcion || undefined,
          noData: 'Sin descripción',
        },
      },
      {
        header: {
          label: 'Acciones',
        },
        row: {
          acciones: [
            // {
            //   tipo: 'material',
            //   color: 'primary',
            //   icon: 'visibility',
            //   tooltip: 'Detalles',
            //   click: (dato) => this.ventanaDocumentos(dato),
            // },
            {
              tipo: 'material',
              color: 'primary',
              icon: 'edit',
              tooltip: 'Editar',
              click: (dato) => this.editar(dato),
              disabled: () =>
                !this.helper.tienePermiso(
                  'Vehículos - Editar documentos conductor',
                  'Vehículos - Editar documentos vehículo',
                ),
            },
            {
              tipo: 'material',
              color: 'warn',
              icon: 'delete',
              tooltip: 'Eliminar',
              click: (dato) => this.eliminar(dato),
              disabled: () =>
                !this.helper.tienePermiso(
                  'Vehículos - Eliminar documentos conductor',
                ),
            },
          ],
        },
      },
    ];
  }

  async ngOnInit() {
    //Nota: tipo indica si es un documento de un usuario o de un activo (vehiculo o colectivo)
    //categoría indica de qué tipo va a ser el recordatorio que se cree el documento (si va a ser un recordatorio del módulo colectivos o choferes)
    //Estos documentos se crean desde detalles de vehículos, colectivos

    if (this.idEntidad) {
      this.id = this.idEntidad;
      this.tipo = this.tipoEntidad;
      this.categoria =
        this.tipo === 'Colectivo'
          ? 'Colectivo'
          : this.tipo === 'Vehiculo'
            ? 'Vehiculo'
            : undefined;
    }
    //Estos documentos se crean desde choferes o conductores
    else {
      const params = await firstValueFrom(this.activatedRoute.paramMap);
      this.id = params.get('id');
      this.tipo = params.get('tipo');
      this.categoria = params.get('categoria') as CategoriaRecordatorio;
    }

    this.setColumnas();
    await this.listar();
  }

  ngOnDestroy() {
    this.documentos$?.unsubscribe();
  }
}
